import { Form, FormProps } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import { Grid2 as Grid, Stack } from "@mui/material";
import { z } from "zod/v4";
import { useState, useRef } from "react";
import DEGeneralInfoFields from "./de-general-info-fields";
import DECondicionPagoFields from "./de-condicion-pago-fields";
import DEArticuloSearch, { DetalleDE } from "./de-articulo-search";
import DEArticuloList from "./de-articulo-list";
import DEComprasPublicasFields from "./de-compras-publicas-fields";
import { facturaElectronicaFormSchema } from "../../schemas";
import { Cliente } from "@/shared/types/cliente";
import { TipoOperacionEnum } from "@/shared/types/contribuyente";
import { useGetEmisoresQuery } from "@/features/configuracion/emisor/api/get-emisor";

const TIPO_TRANSACCION_ANTICIPO = 9;

export const documentoElectronicoFormSchema = facturaElectronicaFormSchema;

interface DocumentoElectronicoFormProps extends FormProps<z.input<typeof documentoElectronicoFormSchema>> {
    onClienteChange?: (cliente: Cliente | null) => void;
    cliente?: Cliente | null;
}

export function DocumentoElectronicoForm({
    form,
    onClienteChange,
    cliente,
    ...props
}: DocumentoElectronicoFormProps) {
    const openModal = useOpenModal();
    const detalleFormRef = useRef<any>(null);
    const [showClienteError, setShowClienteError] = useState(false);
    const { data: emisores } = useGetEmisoresQuery();
    const esAnticipo = emisores?.[0]?.tipoTransaccion === TIPO_TRANSACCION_ANTICIPO;

    // Validar cliente antes de submit
    const handleFormSubmit = () => {
        if (!cliente) {
            setShowClienteError(true);
            return false;
        }
        setShowClienteError(false);
        return true;
    };

    const handleReset = () => {
        form.reset();
        onClienteChange?.(null);
        form.setFieldValue("clienteId", "");
        form.setFieldValue("tipoTransaccion", "2");
        form.setFieldValue("indicadorPresencia", "1");
        form.setFieldValue("condicionOperacion", "contado");
        form.setFieldValue("pagos", [{ tipoPago: "efectivo", monto: 0 }]);
        form.setFieldValue("items", []);
        form.setFieldValue("esB2G", false);
        form.setFieldValue("dModCont", undefined);
        form.setFieldValue("dEntCont", undefined);
        form.setFieldValue("dAnoCont", undefined);
        form.setFieldValue("dSecCont", undefined);
        form.setFieldValue("dFeCodCont", null);
        setShowClienteError(false);
    };

    return <Form form={form} gap={2} {...props}>
        <DEGeneralInfoFields
            onClienteChange={onClienteChange}
            cliente={cliente}
            clienteError={showClienteError}
        />
        <CollapsibleSection title="Items">
            <Grid container columns={{ xs: 1, md: 3 }} spacing={2} flex={1}>
                <Grid component={Stack} size={1}>
                    <DEArticuloSearch
                        ref={detalleFormRef}
                        isB2G={cliente?.tipoOperacion === TipoOperacionEnum.B2G}
                        isAnticipo={esAnticipo}
                        onArticuloSelect={(v: DetalleDE) => {
                            const items = form.getFieldValue("items");
                            const index = items.findIndex(i => i.descripcion === v.descripcion)
                            if (index != -1) {
                                const previousValue = items[index];
                                const newValue = { ...previousValue, cantidad: previousValue.cantidad + v.cantidad }
                                form.replaceFieldValue("items", index, newValue);
                            } else {
                                form.pushFieldValue("items", {
                                    codigo: v.codigo,
                                    descripcion: v.descripcion,
                                    cantidad: v.cantidad,
                                    precio: v.precio,
                                    iva: v.iva,
                                    unidadMedida: v.unidadMedida,
                                    dDncpG: v.dDncpG,
                                    dDncpE: v.dDncpE,
                                    cdcAnticipo: v.cdcAnticipo,
                                });
                            }
                        }} />
                </Grid>
                <Grid component={Stack} size={2} gap={2}>
                    <form.Field name="items" mode="array">
                        {field => <>
                            <DEArticuloList
                                items={(field.state.value || []) as DetalleDE[]}
                                remove={field.removeValue}
                            />
                        </>}
                    </form.Field>
                </Grid>
            </Grid>
        </CollapsibleSection>
        <DECondicionPagoFields />
        {cliente?.tipoOperacion === TipoOperacionEnum.B2G && (
            <DEComprasPublicasFields />
        )}
        <Stack direction={"row"} justifyContent={"end"} gap={2}>
            <Form.Button onClick={() => openModal("confirmReset")}>Cancelar</Form.Button>
            <Form.SubmitButton onClick={handleFormSubmit}>Confirmar</Form.SubmitButton>
        </Stack>

        <ConfirmDialog code="confirmReset">
            <ConfirmDialog.Content
                title="Cancelar Factura Electrónica"
                description="¿Estas seguro que quieres cancelar la factura electrónica actual?"
                onConfirm={handleReset}
            />
        </ConfirmDialog>
    </Form>
}
